/*
 *   Copyright (c) 2021 Busy Human LLC
 *   All rights reserved.
 *   This file, its contents, concepts, methods, behavior, and operation  (collectively the "Software") are protected by trade secret, patent,  and copyright laws. The use of the Software is governed by a license  agreement. Disclosure of the Software to third parties, in any form,  in whole or in part, is expressly prohibited except as authorized by the license agreement.
 */
import { ref, computed } from "vue";
import mitt from "mitt";

export function useAuth( auth, { emit, mode="login", minPasswordScore}) {
    // Data
    var username = ref("");
    var password = ref("");
    var confirmPassword = ref("");
    var validationTimeout = ref(null);
    var validation = ref({
        username        : null,
        password        : null,
        confirmPassword : null
    });
    var error = ref(null);
    var showError = ref(false);
    var loggingIn = ref(false);
    var processing = ref(false);
    var emitter = mitt();


    // Computed
    var passwordLength = computed(() => {
        if (password) {
            return password.value.length;
        } else {
            return 0;
        }
    });
    var formValid = computed(() => {
        switch(mode) {
            case "login":
                return validation.value.username && validation.value.password;
            case "signup":
                return validation.value.username && validation.value.password && validation.value.confirmPassword;
            case "reset":
                return validation.value.password;
        }
    });
    var loginButtonLabel = computed(() => {
        if(loggingIn.value) {
            return "Logging in...";
        } else {
            return "SIGN IN";
        }
    });



    // Methods
    function validateUsername() {
        validation.value.username = username.value.match(/([\w_\-\.]*)@([_\-\.\w]+\.[_\-\w]{2,})/g);
    }
    function validatePassword() {
        validation.value.password = password.value.length >= 7;
    }
    function validateConfirmPassword() {
        validation.value.confirmPassword = confirmPassword.value.length > 4 && password.value == confirmPassword.value;
    }
    function checkValidInput() {
        validateUsername();
        validatePassword();

        let errorMessage = '';

        if(username.value.length > 0 && !validation.value.username) {
            errorMessage = 'Username must be an email address';
            showError.value = true;
        } else if(password.value.length > 0 && !validation.value.password) {
            errorMessage = 'Password must be at least 7 characters long';
            showError.value = true;
        } else {
            showError.value = false;
        }

        if(errorMessage) {
            error.value = '* ' + errorMessage;
        } else {
            error.value = null;
        }
    }
    function validateInputWithDelay() {
        return validateWithDelay( checkValidInput );
    }
    function validateWithDelay( fn ) {
        if(validationTimeout.value) {
            clearTimeout(validationTimeout.value);
            validationTimeout.value = null;
        }
        validationTimeout.value = setTimeout(() => {
            validationTimeout.value = null;
            fn();
        }, 750);
    }
    function onTapLogin() {
        loggingIn.value = true;
        auth.signInWithEmailAndPassword(username.value, password.value)
            .then(result => {
                console.log("Login successful!");
                loggingIn.value = false;
                emit("login", result);
                emitter.emit("login", result);
            })
            .catch(err => {
                // if (err.errorMessage.)
                if(err.stack.includes("Uncaught DOMException: Failed to execute 'put' on")){
                    loggingIn.value = false;
                    emit("login", result);
                    console.log("AUTH ERR", typeof err, err.value, Object.keys(err), JSON.stringify(err), err, );
                    return;
                }
                // auth.esm.js:68 Uncaught DOMException: Failed to execute 'put' on 'IDBObjectStore'
                showError.value = true;
                err.value = "* Incorrect Email Or Password";
                emit("error", err);
                emitter.emit("error", er);
                loggingIn.value = false;
            });
    }

    function onTapCreate() {
        return auth.createUserWithEmailAndPassword(username.value, password.value)
            .then(result => {
                alert("Account created!");
                emit("signup", result);
                emitter.emit("signup", result);
            })
            .catch(err => {
                alert(err);
                emit("error", err);
                emitter.emit("error", err);
            });
    }

    function onTapReset() {
        if(!processing.value) {
            processing.value = true;
            var finishProcessing = () => {
                setTimeout(() => {
                    processing.value = false;
                }, 5000);
            };
            auth.sendPasswordResetEmail(email.value)
                .then(result => {
                    alert("Reset email sent");
                    emit("reset", result);
                    finishProcessing();
                })
                .catch(err => {
                    alert(err);
                    emit("error", err);
                    finishProcessing();
                });
        }
    }

    return {
        emitter,
        username, password, confirmPassword, validationTimeout, validation, error, showError, loggingIn, processing,
        passwordLength, formValid, loginButtonLabel,
        validateWithDelay, validateInputWithDelay, validateUsername, validatePassword, validateConfirmPassword, checkValidInput, onTapLogin, onTapCreate, onTapReset
    };
}

export default useAuth;